import {
  allPurchaseItemsPaid,
  validatePurchaseReceiving,
} from "../../utils/PurchaseReceiving";
import TransactionDetails from "../../components/TransactionDetails";
import EntityLabel from "../../components/EntityLabel";
import { useState } from "react";
import ReturnDialog from "../../components/Returns/ReturnDialog";
import { lookupProduct } from "../../components/InventoryLines/ProductUnitPicker";
import { activeProductUnits } from "../../utils/ProductUnits";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type { Purchase, PurchaseItem } from "../../models/Purchase";
import { Status } from "../../models/Status";
import { PurchaseService } from "../../services/PurchaseService";
import { useInventoryLookups } from "../../hooks/useInventoryLookups";
import {
  PurchaseLines,
  validateInventoryLines,
} from "../../components/InventoryLines/InventoryLines";
import { useAuth } from "../../auth/useAuth";
export default function PurchaseHTML() {
  const { Warehouse } = useWarehouse();
  const [detailsCode, setDetailsCode] = useState("");
  const [returnCode, setReturnCode] = useState("");
  const [revision, setRevision] = useState(0);
  const { products, suppliers, warehouses, error, loading } =
    useInventoryLookups(true);
  const { hasPermission } = useAuth();
  const manage = hasPermission("Purchase.Manage");
  const name = (rows: typeof suppliers, code: string) =>
    String(rows.find((x) => x.Key === code)?.Value ?? code);
  return (
    <>
      <p role={error ? "alert" : "status"}>
        {error || (loading ? "Loading purchase options…" : "")}
      </p>
      <ManagementPageHTML<Purchase>
        key={`${Warehouse?.Code}:${revision}`}
        ActiveRowKey={detailsCode || returnCode}
        RenderRowActions={(row) => (
          <>
            <button
              type="button"
              className="management-action"
              aria-label="View purchase details"
              onClick={() => setDetailsCode(row.Code)}
            >
              <i className="fa-solid fa-eye" />
            </button>
            {row.AddToInventory &&
            (hasPermission("Purchase.Return") ||
              hasPermission("Purchase.ApproveReturn")) ? (
              <button
                type="button"
                className="management-action"
                aria-label="Manage purchase returns"
                onClick={() => setReturnCode(row.Code)}
              >
                <i className="fa-solid fa-rotate-left" />
              </button>
            ) : null}
          </>
        )}
        DialogSections={[
          {
            Title: "Purchase details",
            Keys: [
              "Code",
              "Supplier",
              "WareHouse",
              "TransactionDate",
              "CurrencyCode",
              "Status",
              "PaymentStatus",
            ],
          },
          { Title: "Receiving", Keys: ["AddToInventory"], Side: true },
        ]}
        DialogSummary={(v) => (
          <div className="editor-summary">
            <h3>Purchase summary</h3>
            <p>
              {
                ((v.Items as PurchaseItem[]) ?? []).filter(
                  (x) => x.PaymentStatus === "Paid",
                ).length
              }{" "}
              of {((v.Items as PurchaseItem[]) ?? []).length} items paid
            </p>
            <div>
              <span>Product lines</span>
              <strong>{((v.Items as PurchaseItem[]) ?? []).length}</strong>
            </div>
            <div className="editor-summary-total">
              <span>Total cost</span>
              <strong>
                {new Intl.NumberFormat("en-NG", {
                  style: "currency",
                  currency: "NGN",
                }).format(
                  ((v.Items as PurchaseItem[]) ?? []).reduce(
                    (sum, x) => sum + Number(x.Amount || 0),
                    0,
                  ),
                )}
              </strong>
            </div>
            <p>
              Receiving updates stock and cost price. Selling prices are managed
              on the product.
            </p>
          </div>
        )}
        Title="Purchases"
        Eyebrow="Procurement"
        Description="Record supplier purchases and optionally add received goods to inventory. Posted purchases are locked to prevent adding stock twice."
        AddLabel="Add purchase"
        SearchPlaceholder="Search purchase or supplier name"
        Rows={[]}
        Summaries={[]}
        CanExport={false}
        Service={PurchaseService(Warehouse?.Code)}
        CanCreate={manage && !loading && !error}
        CanEdit={manage && !loading && !error}
        CanDelete={manage}
        CanEditRow={(x) => !x.AddToInventory}
        CanDeleteRow={(x) => !x.AddToInventory}
        DefaultValues={{
          WareHouse: Warehouse?.Code,
          Items: [],
          Status: Status.Active,
          PaymentStatus: "Paid",
          AddToInventory: false,
          CurrencyCode: "NGN",
          TransactionDate: new Date().toISOString(),
        }}
        Statuses={Object.values(Status)}
        GetStatus={(x) => x.Status}
        GetRowKey={(x) => x.Code}
        GetSearchText={(x) => `${x.Code} ${x.Supplier} ${x.WareHouse ?? ""}`}
        Columns={[
          {
            Key: "Code",
            Header: "Purchase",
            Render: (x) => (
              <IdentityCell
                Icon="fa-file-invoice"
                Title={x.Code}
                Subtitle={`${x.Items?.length ?? 0} products`}
              />
            ),
          },
          {
            Key: "Supplier",
            Header: "Supplier",
            Render: (x) => <EntityLabel entity="supplier" code={x.Supplier} />,
          },
          {
            Key: "WareHouse",
            Header: "Warehouse",
            Render: (x) => (
              <EntityLabel entity="warehouse" code={x.WareHouse} />
            ),
          },
          {
            Key: "Items",
            Header: "Items",
            Render: (x) => (
              <details>
                <summary>View {x.Items?.length ?? 0} lines</summary>
                <ul>
                  {(x.Items ?? []).map((l, i) => (
                    <li key={i}>
                      {<EntityLabel entity="product" code={l.ProductCode} />} ·
                      quantity {l.Quantity} · unit {l.UnitCode || "conversion"}{" "}
                      ({l.BaseUnitQuantity} base units each) · amount {l.Amount}{" "}
                      · {l.PaymentStatus} · {l.Status}
                    </li>
                  ))}
                </ul>
              </details>
            ),
          },
          {
            Key: "Payment",
            Header: "Payment",
            Render: (x) => <StatusBadge Status={x.PaymentStatus} />,
          },
          {
            Key: "Posted",
            Header: "Stock",
            Render: (x) =>
              x.AddToInventory ? "Posted · locked" : "Not posted",
          },
          {
            Key: "Status",
            Header: "Status",
            Render: (x) => <StatusBadge Status={x.Status} />,
          },
        ]}
        DialogFields={[
          { Key: "Code", Label: "Purchase code", Disable: true },
          {
            Key: "TransactionDate",
            Label: "Purchase date",
            Type: "datetime",
            Required: true,
          },
          { Key: "CurrencyCode", Label: "Currency", Required: true },
          {
            Key: "Supplier",
            Label: "Supplier",
            Required: true,
            Type: "select",
            Options: suppliers.map((x) => ({
              display: String(x.Value),
              value: x.Key,
              other: null,
            })),
          },
          {
            Key: "WareHouse",
            Disable: true,
            Label: "Warehouse",
            Required: true,
            Type: "select",
            Options: warehouses.map((x) => ({
              display: String(x.Value),
              value: x.Key,
              other: null,
            })),
          },
          {
            Key: "PaymentStatus",
            Label: "Payment status",
            Required: true,
            Type: "select",
            Options: ["Pending", "PartiallyPaid", "Paid", "Failed", "Refunded"],
          },
          {
            Key: "Status",
            Label: "Status",
            Required: true,
            Type: "select",
            Options: Object.values(Status),
          },
          {
            Key: "AddToInventory",
            Label: "Add received goods to inventory",
            DisabledWhen: (v) =>
              !allPurchaseItemsPaid((v.Items ?? []) as PurchaseItem[]),
            Type: "switch",
            HelpText:
              "All items must be Paid before adding stock. Enable only when goods have arrived; posting locks the purchase.",
          },
        ]}
        OnFormValueChange={(field, value) =>
          field === "Items" && !allPurchaseItemsPaid(value as PurchaseItem[])
            ? { AddToInventory: false }
            : undefined
        }
        ValidateForm={(v): Record<string, string> => {
          const receivingError = validatePurchaseReceiving(
            (v.Items ?? []) as PurchaseItem[],
            Boolean(v.AddToInventory),
          );
          if (receivingError) return { AddToInventory: receivingError };
          let msg = validateInventoryLines(v.Items, true);
          if (!msg)
            for (const line of v.Items as PurchaseItem[]) {
              const options = activeProductUnits(
                lookupProduct(products, line.ProductCode),
              );
              const matches = options.filter((unit) =>
                line.UnitCode
                  ? unit.UnitCode === line.UnitCode &&
                    unit.BaseQuantity === line.BaseUnitQuantity
                  : unit.BaseQuantity === line.BaseUnitQuantity,
              );
              if (matches.length !== 1) {
                msg = "Select an active product unit on every purchase line.";
                break;
              }
            }
          return msg ? { Items: msg } : {};
        }}
        RenderDialogContent={({
          FormValues,
          SetFormValue,
          ValidationErrors,
        }) => (
          <>
            <PurchaseLines
              items={(FormValues.Items ?? []) as PurchaseItem[]}
              products={products}
              onChange={(x) => SetFormValue("Items", x)}
            />
            {ValidationErrors.Items && (
              <p role="alert">{ValidationErrors.Items}</p>
            )}
            {detailsCode && (
              <TransactionDetails
                kind="purchase"
                code={detailsCode}
                onClose={() => setDetailsCode("")}
              />
            )}
            {returnCode && (
              <ReturnDialog
                key={Warehouse?.Code}
                kind="purchase"
                code={returnCode}
                onClose={() => setReturnCode("")}
                onChanged={() => setRevision((x) => x + 1)}
              />
            )}
          </>
        )}
        GetSummaries={(rows, total) => [
          {
            Label: "Purchases",
            Value: total,
            Icon: "fa-file-invoice",
            Tone: "Blue",
          },
          {
            Label: "Posted on this page",
            Value: rows.filter((x) => x.AddToInventory).length,
            Icon: "fa-warehouse",
            Tone: "Green",
          },
          {
            Label: "Awaiting payment on this page",
            Value: rows.filter(
              (x) =>
                x.PaymentStatus === "Pending" ||
                x.PaymentStatus === "PartiallyPaid",
            ).length,
            Icon: "fa-clock",
            Tone: "Orange",
          },
        ]}
      />
      {detailsCode && (
        <TransactionDetails
          kind="purchase"
          code={detailsCode}
          onClose={() => setDetailsCode("")}
        />
      )}
      {returnCode && (
        <ReturnDialog
          key={Warehouse?.Code}
          kind="purchase"
          code={returnCode}
          onClose={() => setReturnCode("")}
          onChanged={() => setRevision((x) => x + 1)}
        />
      )}
    </>
  );
}
